
import React, { useState } from 'react';
import { Patient, Plan, Appointment, Reminder } from '../../types';
import { 
  X, Apple, Calendar, Bell, 
  CheckCircle2, Clock, AlertCircle, 
  TrendingUp, CalendarDays
} from 'lucide-react';
import { useData } from '../../context/DataContext';
import { formatCurrency } from '../../utils/businessLogic';
import { calculateAge } from '../../utils/calculations';

interface PatientDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  patient: Patient;
}

const PatientDetailsModal: React.FC<PatientDetailsModalProps> = ({ isOpen, onClose, patient }) => {
  const { plans, appointments, reminders } = useData();
  const [activeTab, setActiveTab] = useState('plans');

  if (!isOpen) return null;
  
  const patientAge = calculateAge(patient.birthDate);

  // Filtros de dados específicos do paciente
  const patientPlans = plans.filter(p => p.patientId === patient.id);
  const patientAppointments = appointments
    .filter(a => a.patientId === patient.id)
    .sort((a, b) => `${b.date}T${b.time}`.localeCompare(`${a.date}T${a.time}`));
  const patientReminders = reminders.filter(r => r.patientId === patient.id);
  
  const renderTabContent = () => {
    switch(activeTab) {
      case 'plans':
        return (
          <div className="space-y-4">
            {patientPlans.length === 0 ? (
              <div className="text-center py-8 text-slate-400 italic bg-white rounded-lg border border-dashed border-slate-200">
                Este paciente ainda não possui planos contratados.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {patientPlans.map(plan => (
                  <div key={plan.id} className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-bold text-slate-800">{plan.type}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                          plan.status === 'Ativo' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                        }`}>{plan.status}</span>
                      </div>
                      <p className="text-xs text-slate-500">
                        Vigência: {plan.startDate.split('-').reverse().join('/')} a {plan.endDate.split('-').reverse().join('/')}
                      </p>
                    </div>
                    <div className="text-right flex sm:flex-col items-center sm:items-end justify-between w-full sm:w-auto gap-2">
                       <span className="text-sm font-bold text-indigo-600">{formatCurrency(plan.value)}</span>
                       <span className="text-[10px] text-slate-400">Sessões: {plan.consultations.filter(c => c.completed).length}/{plan.sessions}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      case 'appointments':
        return (
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden shadow-sm">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase">Data/Hora</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase">Tipo</th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-slate-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {patientAppointments.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-slate-400 italic">
                      Nenhum agendamento registrado.
                    </td>
                  </tr>
                ) : (
                  patientAppointments.map(apt => (
                    <tr key={apt.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700 font-medium">
                        {apt.date.split('-').reverse().join('/')} <span className="text-slate-400 ml-1">{apt.time}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                        {apt.type}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <span className={`text-[10px] px-2 py-1 rounded-full font-bold uppercase ${
                          apt.status === 'Realizado' ? 'bg-green-100 text-green-700' :
                          apt.status === 'Agendado' ? 'bg-amber-100 text-amber-700' :
                          apt.status === 'Confirmado' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {apt.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        );
      case 'reminders':
        return (
          <div className="space-y-3">
            {patientReminders.length === 0 ? (
              <div className="text-center py-8 text-slate-400 italic bg-white rounded-lg border border-dashed border-slate-200">
                Sem lembretes para este paciente.
              </div>
            ) : (
              patientReminders.map(reminder => (
                <div key={reminder.id} className={`p-4 rounded-lg border shadow-sm flex items-start gap-3 bg-white ${
                  reminder.priority === 'Pendência Urgente' ? 'border-l-4 border-l-red-500 border-slate-200' : 'border-l-4 border-l-amber-400 border-slate-200'
                }`}>
                  <div className={`mt-1 ${reminder.priority === 'Pendência Urgente' ? 'text-red-500' : 'text-amber-500'}`}>
                    <AlertCircle size={18} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800">{reminder.text}</p>
                    <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider font-bold">
                      Prazo: {reminder.date.split('-').reverse().join('/')}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        );
      default:
        return <div className="p-4 text-center">Selecione uma aba para visualizar os dados.</div>;
    }
  }

  const tabs = [
      { id: 'plans', label: 'Planos', icon: Apple },
      { id: 'appointments', label: 'Agendamentos', icon: Calendar },
      { id: 'reminders', label: 'Lembretes', icon: Bell },
  ];

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex justify-center items-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-slate-50 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col relative animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-white p-6 border-b border-slate-200 rounded-t-2xl">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold text-2xl border-2 border-white shadow-sm">
                {patient.name.charAt(0)}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 leading-tight">{patient.name}</h2>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500 mt-1">
                  <span className="flex items-center gap-1"><CalendarDays size={14}/> {patientAge > 0 ? `${patientAge} anos` : '-'}</span>
                  <span className="flex items-center gap-1 font-medium text-indigo-600 underline decoration-indigo-200 underline-offset-2">{patient.phone}</span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700 hover:bg-slate-100 p-2 rounded-full transition-all"
            >
              <X size={24} />
            </button>
          </div>
        </div>
        
        <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm z-10">
           <nav className="flex space-x-8 px-8" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-bold text-xs uppercase tracking-widest flex items-center gap-2 transition-all`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex-grow overflow-y-auto p-6 bg-slate-50/50">
          <div className="max-w-3xl mx-auto">
            {renderTabContent()}
          </div>
        </div>

        <div className="bg-white p-4 border-t border-slate-100 rounded-b-2xl flex justify-between items-center px-8">
           <p className="text-[10px] text-slate-400 uppercase font-bold tracking-tighter">4Nutri v2.1 • Cadastro em {new Date(patient.createdAt).toLocaleDateString()}</p>
           <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${patientPlans.some(p => p.status === 'Ativo') ? 'bg-green-500' : 'bg-slate-300'}`}></div>
              <span className="text-[10px] font-bold text-slate-500 uppercase">{patientPlans.some(p => p.status === 'Ativo') ? 'Paciente Ativo' : 'Sem Plano Ativo'}</span>
           </div>
        </div>
      </div>
    </div>
  );
};

export default PatientDetailsModal;
